import { createFileRoute } from "@tanstack/react-router";
import { LegalPage } from "@/components/legal-page";
import { socialImageMeta, breadcrumbLd } from "@/lib/seo";

export const Route = createFileRoute("/legal/refunds")({
  head: () => ({
    meta: [
      { title: "Refund & Dispute Policy — BidNova Auctions" },
      { name: "description", content: "How refunds, returns and disputes are handled on BidNova Auctions." },
      { property: "og:title", content: "Refund & Dispute Policy — BidNova Auctions" },
      { property: "og:description", content: "How refunds, returns and disputes are handled on BidNova Auctions." },
      { property: "og:url", content: "https://bidnovaauctions.uk/legal/refunds" },
      { property: "og:type", content: "article" },
      ...socialImageMeta(),
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://bidnovaauctions.uk/legal/refunds" }],
    scripts: [breadcrumbLd([{ name: "Home", path: "/" }, { name: "Legal", path: "/legal/terms" }, { name: "Refunds", path: "/legal/refunds" }])],
  }),
  component: Refunds,
});

function Refunds() {
  return (
    <LegalPage eyebrow="Legal" title="Refund &amp; Dispute Policy" updated="7 July 2026">
      <p>
        This policy explains how refunds, returns and disputes are managed on BidNova Auctions. It applies to
        every completed order and forms part of our{" "}
        <a href="/legal/terms">Terms &amp; Conditions</a>.
      </p>

      <h2>1. Statutory Rights</h2>
      <p>
        Nothing in this policy limits your statutory rights under the Consumer Rights Act 2015, the Consumer
        Contracts Regulations 2013 (where applicable), or the Sale of Goods Act 1979.
      </p>

      <h2>2. Buyer Protection Window</h2>
      <ul>
        <li>You have <strong>7 days from delivery</strong> to inspect the item and raise a dispute.</li>
        <li>During this period the platform continues to hold the seller's funds. Payouts are only released once you confirm delivery, the dispute window closes without a claim, or a dispute is resolved in the seller's favour.</li>
      </ul>

      <h2>3. Grounds for a Dispute</h2>
      <ol>
        <li><strong>Item not received</strong> — no delivery within a reasonable time after dispatch and tracking shows the item did not arrive.</li>
        <li><strong>Not as described</strong> — grading, edition, language, condition, or authenticity differs materially from the listing.</li>
        <li><strong>Damaged in transit</strong> — evidence of damage on arrival (photos required).</li>
        <li><strong>Counterfeit</strong> — item is a proven counterfeit or reproduction.</li>
      </ol>

      <h2>4. How to Open a Dispute</h2>
      <ol>
        <li>Go to <em>My orders → open dispute</em> within 7 days of delivery.</li>
        <li>Provide a written description and, where relevant, clear photographs and packaging shots.</li>
        <li>The seller has 5 working days to respond and offer a remedy.</li>
      </ol>

      <h2>5. Resolution &amp; Outcomes</h2>
      <p>Where the parties cannot agree, our disputes team will review the evidence and decide one of the following:</p>
      <ul>
        <li><strong>Refund the buyer</strong> — full refund issued from held funds; the buyer must return the item (tracked, at the seller's expense) unless we agree otherwise.</li>
        <li><strong>Release to the seller</strong> — payout continues as normal; the buyer's claim is dismissed.</li>
        <li><strong>Partial refund</strong> — where a mutually agreed reduction reflects the issue (e.g. minor cosmetic damage).</li>
        <li><strong>Dismiss the dispute</strong> — where the claim is unsupported by evidence.</li>
      </ul>
      <p>Our decision is issued within 10 working days of dispute closure and is final on the platform. It does not affect your right to pursue legal remedies.</p>

      <h2>6. Return Shipping</h2>
      <p>
        Where a return is required, the buyer must ship the item within 5 working days of the resolution, using a
        tracked service. Refunds are issued once tracking confirms the seller has received the item in the same condition.
      </p>

      <h2>7. Chargebacks</h2>
      <p>
        You must attempt to resolve disputes on-platform before initiating a chargeback with your bank or card issuer.
        Chargebacks filed while a dispute is open may result in suspension.
      </p>

      <h2>8. Cancellations Before Payment</h2>
      <p>
        You may only withdraw from a winning bid in exceptional circumstances (e.g. bidder error confirmed within 15 minutes,
        or seller misrepresentation). Contact support immediately.
      </p>

      <h2>9. Fees</h2>
      <ul>
        <li>Buyers pay no dispute fee.</li>
        <li>Sellers found liable for a dispute may forfeit the listing fee and be charged for return postage.</li>
      </ul>

      <h2>10. Complaints &amp; ADR</h2>
      <p>
        If you are unhappy with our resolution, contact <a href="mailto:disputes@bidnovaauctions.uk">disputes@bidnovaauctions.uk</a>.
        Where we cannot resolve a complaint you may refer the matter to a certified Alternative Dispute Resolution
        provider and, if applicable, use the EU Online Dispute Resolution platform at{" "}
        <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noreferrer">ec.europa.eu/consumers/odr</a>.
      </p>

      <h2>11. Governing Law</h2>
      <p>This policy is governed by the laws of England &amp; Wales.</p>
    </LegalPage>
  );
}
